/*
 * OPTIMUS GRADING CONTRACT — PRESENTATION MIRROR (PHASE 1)
 * =======================================================
 *
 * MIRROR — DO NOT EDIT BY HAND.
 *
 * The authority for Optimus grading semantics is the backend module
 * `services/optimusGradingContract.js`. This file is the presentation-safe
 * projection of that contract, and must stay byte-identical between
 * client/src/utils and superadmin/src/utils. Parity is enforced by the backend
 * suite `tests/optimusGradingContractParity.test.js`, which fails the build if
 * this projection drifts from the authority or between the two portals.
 *
 * Grade MATH lives in the backend. What lives here is only what the UI needs to
 * render honestly: which metrics are applicable, which carry a usable
 * benchmark, and which must be shown as NOT APPLICABLE / NOT MEASURED /
 * NO VALID BENCHMARK instead of a red-or-green verdict.
 *
 * Phase 1 rules this projection encodes:
 *   - the generic ALL cpm [6,22] / ctr [1.0,2.0] bands are gone, not replaced;
 *   - no benchmark is recommendation-grade, so no benchmark-relative advice
 *     may be rendered;
 *   - unresolved platforms have no profile and must not fall back to ALL.
 */

export const OPTIMUS_GRADING_METHODOLOGY_VERSION = "optimus_grading_safety_v1";
export const OPTIMUS_LEGACY_GRADING_METHODOLOGY_VERSION = "optimus_grading_generic_v0";

export const OPTIMUS_METRIC_APPLICABILITY = Object.freeze({
  "PRIMARY": "PRIMARY",
  "SECONDARY": "SECONDARY",
  "CONTEXT_ONLY": "CONTEXT_ONLY",
  "NOT_APPLICABLE": "NOT_APPLICABLE"
});

export const OPTIMUS_METRIC_STATE = Object.freeze({
  "MEASURED": "MEASURED",
  "NOT_MEASURED": "NOT_MEASURED",
  "NOT_APPLICABLE": "NOT_APPLICABLE",
  "NO_BENCHMARK": "NO_BENCHMARK"
});

export const OPTIMUS_METRIC_STATE_LABELS = Object.freeze({
  "MEASURED": "Measured",
  "NOT_MEASURED": "NOT MEASURED",
  "NOT_APPLICABLE": "NOT APPLICABLE",
  "NO_BENCHMARK": "NO VALID BENCHMARK"
});

export const OPTIMUS_BENCHMARK_AUTHORITY = Object.freeze({
  "GOVERNED_CAMPAIGN_TARGET": "GOVERNED_CAMPAIGN_TARGET",
  "CPHERE_HISTORICAL": "CPHERE_HISTORICAL",
  "PROVIDER_PUBLISHED": "PROVIDER_PUBLISHED",
  "LEGACY_UNVERIFIED": "LEGACY_UNVERIFIED",
  "LEGACY_GENERIC_COPY": "LEGACY_GENERIC_COPY",
  "PLAN_TRUTH": "PLAN_TRUTH",
  "NONE": "NONE"
});

export const OPTIMUS_GRADING_BASIS_STATE = Object.freeze({
  "PRE_LIVE": "PRE_LIVE",
  "AWAITING_DELIVERY": "AWAITING_DELIVERY",
  "LIVE_GRADED": "LIVE_GRADED",
  "LIVE_UNGRADED_NO_VALID_BENCHMARK": "LIVE_UNGRADED_NO_VALID_BENCHMARK",
  "LIVE_INSUFFICIENT_GRADING_BASIS": "LIVE_INSUFFICIENT_GRADING_BASIS",
  "UNSUPPORTED_GRADING_PROFILE": "UNSUPPORTED_GRADING_PROFILE"
});

export const OPTIMUS_GRADING_BASIS_LABELS = Object.freeze({
  "PRE_LIVE": "PL",
  "AWAITING_DELIVERY": "AD",
  "LIVE_GRADED": null,
  "LIVE_UNGRADED_NO_VALID_BENCHMARK": "NO VALID BENCHMARK",
  "LIVE_INSUFFICIENT_GRADING_BASIS": "GRADING BASIS INCOMPLETE",
  "UNSUPPORTED_GRADING_PROFILE": "UNSUPPORTED GRADING PROFILE"
});

/*
 * Legacy Signal Quality thresholds. Retained for disclosure only: they have no
 * platform or objective provenance and contribute no grade weight in Phase 1.
 */
export const OPTIMUS_SIGNAL_QUALITY_CONTRACT = Object.freeze({
  "authority": "LEGACY_UNVERIFIED",
  "gradingEnabled": false,
  "informationalOnly": true,
  "confidence": "UNVERIFIED",
  "version": "optimus_grading_safety_v1",
  "provenance": "Hardcoded thresholds with no platform or objective provenance. Suppressed from grade weight in Phase 1.",
  "legacyThresholds": {
    "viewRate": [
      3,
      6
    ],
    "engagementRate": [
      2,
      5
    ],
    "defaultPoints": 70,
    "legacyWeight": 0.1
  }
});

/* One platform => one profile. Mirrors the backend normalizer exactly. */
export const OPTIMUS_PLATFORM_KEY_ALIASES = Object.freeze({
  "yahoo": "yahoo_dsp",
  "yahoo!": "yahoo_dsp",
  "yahoo_dsp": "yahoo_dsp",
  "yahoodsp": "yahoo_dsp",
  "yahoo!_dsp": "yahoo_dsp",
  "yahoo_dsp_display": "yahoo_dsp",
  "yahoo_dsp_ctv": "yahoo_dsp",
  "viant": "viant",
  "viant_dsp": "viant",
  "viantdsp": "viant",
  "viant_dsp_(display)": "viant",
  "viant_display": "viant",
  "viant_dsp_ctv": "viant",
  "googlesearch": "google_search",
  "google_search": "google_search",
  "search": "google_search",
  "sem": "google_search",
  "paid_search": "google_search",
  "adwords": "google_search",
  "google_ads": "google_search",
  "truthsocial": "truth_social",
  "truth_social": "truth_social",
  "chatgptsearch": "chatgpt_search",
  "chatgpt_search": "chatgpt_search",
  "openaisearch": "chatgpt_search",
  "connectedtv": "ctv",
  "connected_tv": "ctv",
  "ctv": "ctv",
  "streaming_tv": "ctv",
  "ott": "ctv",
  "olv": "video",
  "online_video": "video",
  "preroll": "video",
  "pre_roll": "video",
  "video": "video",
  "youtube": "youtube",
  "yt": "youtube",
  "facebook": "facebook",
  "fb": "facebook",
  "instagram": "instagram",
  "ig": "instagram",
  "audience_network": "audience_network",
  "audiencenetwork": "audience_network",
  "messenger": "messenger",
  "tiktok": "tiktok",
  "display": "display",
  "programmatic": "display",
  "display_retargeting": "display",
  "mobile": "mobile",
  "dooh": "dooh",
  "ooh": "dooh",
  "audio": "audio",
  "streaming_audio": "audio",
  "ingame": "ingame",
  "in_game": "ingame",
  "email": "email",
  "reddit": "reddit",
  "amazon": "amazon",
  "amazon_ads": "amazon",
  "x": "x",
  "twitter": "x",
  "rumble": "rumble",
  "cinema": "cinema",
  "theatrical": "cinema",
  "linkedin": "linkedin",
  "google_analytics": "google_analytics",
  "googleanalytics": "google_analytics",
  "ga4": "google_analytics",
  "ga": "google_analytics",
  "analytics": "google_analytics",
  "site_analytics": "google_analytics",
  "siteanalytics": "google_analytics",
  "shopify": "shopify",
  "all": "all",
  "all_platforms": "all",
  "omni": "all"
});

export const OPTIMUS_PLATFORM_GRADING_CONTRACT = Object.freeze({
  "yahoo_dsp": {
    "key": "yahoo_dsp",
    "label": "Yahoo!",
    "supported": true,
    "platformGradingEnabled": false,
    "primaryMetrics": [],
    "ungradedReason": "Provider reporting is available when scoped evidence exists; inventory-specific grading and optimization capability are not established.",
    "metrics": {
      "impressions": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "clicks": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "spend": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "cpm": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "ctr": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "cpc": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "conversions": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "completionRate": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "viewRate": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "reach": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "frequency": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      }
    }
  },
  "viant": {
    "key": "viant",
    "label": "Viant",
    "supported": true,
    "platformGradingEnabled": false,
    "primaryMetrics": [],
    "ungradedReason": "Provider reporting is available when scoped evidence exists; inventory-specific grading and optimization capability are not established.",
    "metrics": {
      "impressions": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "clicks": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "spend": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "cpm": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "ctr": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "cpc": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "conversions": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "completionRate": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "viewRate": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "reach": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      },
      "frequency": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Provider totals may combine inventory with different metric semantics. No governed benchmark is established."
      }
    }
  },
  "all": {
    "key": "all",
    "label": "All Platforms",
    "supported": true,
    "platformGradingEnabled": false,
    "primaryMetrics": [],
    "ungradedReason": "Blended cross-platform performance has no valid benchmark. The generic CPM/CTR bands were removed in Phase 1; the unified campaign model is Phase 2.",
    "metrics": {
      "cpm": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Blended eCPM is reportable but not benchmarkable across a mixed inventory basis."
      },
      "ctr": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Blended CTR spans click-bearing and non-click-bearing channels."
      },
      "completionRate": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      },
      "viewRate": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "ctv": {
    "key": "ctv",
    "label": "CTV",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "completionRate"
    ],
    "ungradedReason": null,
    "metrics": {
      "ctr": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "CTV inventory is non-click-bearing."
      },
      "cpc": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "CTV inventory is non-click-bearing."
      },
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          18,
          45
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          90,
          98
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "viewRate": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          90,
          100
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": "Starts-as-views basis. Not a generic VVR band; never score a generic VVR with it."
      },
      "cpcv": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "qvv": {
        "applicability": "SECONDARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      }
    }
  },
  "youtube": {
    "key": "youtube",
    "label": "YouTube",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpqv",
      "viewRate",
      "qvv"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpqv": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "cpv": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "qvv": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "viewRate": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "completionRate": {
        "applicability": "SECONDARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "cpm": {
        "applicability": "SECONDARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          8,
          28
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.4,
          1
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "display": {
    "key": "display",
    "label": "Display",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          2,
          10
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.2,
          0.8
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Display inventory is non-video by default."
      },
      "siteVisits": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Governed site-visit outcomes carry no benchmark. Phase 2."
      }
    }
  },
  "google_search": {
    "key": "google_search",
    "label": "Google Search",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpc"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpc": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "cpm": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Search is bought on clicks, not impressions."
      },
      "ctr": {
        "applicability": "SECONDARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          2.5,
          6
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "conversionRate": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "completionRate": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Search inventory is non-video."
      }
    }
  },
  "tiktok": {
    "key": "tiktok",
    "label": "TikTok",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "completionRate",
      "cpm"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          5,
          18
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "ctr": {
        "applicability": "SECONDARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.8,
          1.8
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "facebook": {
    "key": "facebook",
    "label": "Facebook",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          6,
          20
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          1,
          2.2
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "SECONDARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "instagram": {
    "key": "instagram",
    "label": "Instagram",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          7,
          24
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.9,
          2
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "SECONDARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "audience_network": {
    "key": "audience_network",
    "label": "Audience Network",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          3,
          14
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.3,
          1.2
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Off-platform placement mix is not video-guaranteed."
      }
    }
  },
  "messenger": {
    "key": "messenger",
    "label": "Messenger",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          4,
          16
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.6,
          1.6
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "mobile": {
    "key": "mobile",
    "label": "Mobile",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          4,
          16
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.6,
          1.6
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "video": {
    "key": "video",
    "label": "Online Video",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "completionRate"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          8,
          25
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          70,
          90
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.3,
          0.9
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "dooh": {
    "key": "dooh",
    "label": "DOOH",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          8,
          35
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "DOOH inventory is non-click-bearing."
      },
      "cpc": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "DOOH inventory is non-click-bearing."
      },
      "completionRate": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "cinema": {
    "key": "cinema",
    "label": "Cinema",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          12,
          40
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Cinema inventory is non-click-bearing."
      },
      "cpc": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Cinema inventory is non-click-bearing."
      }
    }
  },
  "linkedin": {
    "key": "linkedin",
    "label": "LinkedIn",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          20,
          80
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.35,
          1.2
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "ingame": {
    "key": "ingame",
    "label": "In-Game",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          3,
          14
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.2,
          0.8
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "rumble": {
    "key": "rumble",
    "label": "Rumble",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          8,
          25
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.3,
          0.9
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "completionRate": {
        "applicability": "SECONDARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "x": {
    "key": "x",
    "label": "X",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_GENERIC_COPY",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          6,
          22
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": "Suppressed from grading: identical to the generic band Phase 1 removes."
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.6,
          1.4
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "truth_social": {
    "key": "truth_social",
    "label": "Truth Social",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_GENERIC_COPY",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          6,
          22
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": "Suppressed from grading: identical to the generic band Phase 1 removes."
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.6,
          1.6
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "amazon": {
    "key": "amazon",
    "label": "Amazon",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_GENERIC_COPY",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          6,
          22
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": "Suppressed from grading: identical to the generic band Phase 1 removes."
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.2,
          0.8
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "audio": {
    "key": "audio",
    "label": "Audio",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_GENERIC_COPY",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          6,
          22
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": "Suppressed from grading: identical to the generic band Phase 1 removes."
      },
      "ctr": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Audio inventory is non-click-bearing."
      },
      "cpc": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Audio inventory is non-click-bearing."
      },
      "completionRate": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      }
    }
  },
  "reddit": {
    "key": "reddit",
    "label": "Reddit",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpm",
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpm": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          4,
          18
        ],
        "unit": "USD_PER_MILLE",
        "direction": "lower_is_better",
        "note": null
      },
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          0.3,
          1
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      }
    }
  },
  "email": {
    "key": "email",
    "label": "Email",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "ctr"
    ],
    "ungradedReason": null,
    "metrics": {
      "ctr": {
        "applicability": "PRIMARY",
        "hasBenchmark": true,
        "authority": "LEGACY_UNVERIFIED",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": [
          1.5,
          4.5
        ],
        "unit": "PERCENT",
        "direction": "higher_is_better",
        "note": null
      },
      "cpm": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Email is not bought on impressions."
      },
      "completionRate": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": null
      }
    }
  },
  "google_analytics": {
    "key": "google_analytics",
    "label": "Google Analytics",
    "supported": true,
    "platformGradingEnabled": false,
    "primaryMetrics": [],
    "ungradedReason": "GA4 is a measurement surface, not a media channel. It carries no media grade.",
    "metrics": {
      "cpm": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Not a media channel."
      },
      "ctr": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Not a media channel."
      }
    }
  },
  "shopify": {
    "key": "shopify",
    "label": "Shopify",
    "supported": true,
    "platformGradingEnabled": false,
    "primaryMetrics": [],
    "ungradedReason": "Shopify is a commerce/measurement surface, not a media channel. It carries no media grade.",
    "metrics": {
      "cpm": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Not a media channel."
      },
      "ctr": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Not a media channel."
      },
      "roas": {
        "applicability": "CONTEXT_ONLY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      }
    }
  },
  "chatgpt_search": {
    "key": "chatgpt_search",
    "label": "ChatGPT Search",
    "supported": true,
    "platformGradingEnabled": true,
    "primaryMetrics": [
      "cpc"
    ],
    "ungradedReason": null,
    "metrics": {
      "cpc": {
        "applicability": "PRIMARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "ctr": {
        "applicability": "SECONDARY",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "No benchmark exists. Phase 2."
      },
      "cpm": {
        "applicability": "NOT_APPLICABLE",
        "hasBenchmark": false,
        "authority": "NONE",
        "gradingEnabled": false,
        "recommendationEnabled": false,
        "band": null,
        "unit": null,
        "direction": null,
        "note": "Bought on clicks, not impressions."
      }
    }
  }
});

export const normalizeOptimusPlatformKey = (raw) => {
  const input = String(raw == null ? "" : raw)
    .trim()
    .toLowerCase()
    .replace(/[\s\-.]+/g, "_")
    .replace(/_+/g, "_");

  if (!input) return "";
  return OPTIMUS_PLATFORM_KEY_ALIASES[input] || input;
};

/* No silent fallback to the ALL profile: an unmapped platform returns null. */
export const getOptimusPlatformContract = (rawKey) => {
  const key = normalizeOptimusPlatformKey(rawKey);
  if (!key) return null;
  return OPTIMUS_PLATFORM_GRADING_CONTRACT[key] || null;
};

export const getOptimusMetricContract = (rawKey, metric) => {
  const profile = getOptimusPlatformContract(rawKey);
  if (!profile) return null;
  return profile.metrics[metric] || null;
};

/*
 * Resolve what the UI should render for a metric. Returns a state rather than
 * a tone so the surface can never paint a red/green verdict it cannot defend.
 */
export const resolveOptimusMetricPresentation = (rawKey, metric, value) => {
  const profile = getOptimusPlatformContract(rawKey);
  if (!profile) {
    return {
      state: OPTIMUS_METRIC_STATE.NO_BENCHMARK,
      label: OPTIMUS_GRADING_BASIS_LABELS.UNSUPPORTED_GRADING_PROFILE,
      gradable: false,
      showVerdict: false,
    };
  }

  const contract = profile.metrics[metric];
  if (!contract || contract.applicability === OPTIMUS_METRIC_APPLICABILITY.NOT_APPLICABLE) {
    return {
      state: OPTIMUS_METRIC_STATE.NOT_APPLICABLE,
      label: OPTIMUS_METRIC_STATE_LABELS.NOT_APPLICABLE,
      gradable: false,
      showVerdict: false,
      note: contract ? contract.note : null,
    };
  }

  if (!contract.hasBenchmark || !contract.gradingEnabled) {
    return {
      state: OPTIMUS_METRIC_STATE.NO_BENCHMARK,
      label: OPTIMUS_METRIC_STATE_LABELS.NO_BENCHMARK,
      gradable: false,
      showVerdict: false,
      note: contract.note,
    };
  }

  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      state: OPTIMUS_METRIC_STATE.NOT_MEASURED,
      label: OPTIMUS_METRIC_STATE_LABELS.NOT_MEASURED,
      gradable: false,
      showVerdict: false,
      note: contract.note,
    };
  }

  return {
    state: OPTIMUS_METRIC_STATE.MEASURED,
    label: null,
    gradable: true,
    showVerdict: true,
    band: contract.band,
    unit: contract.unit,
    direction: contract.direction,
    authority: contract.authority,
    note: contract.note,
  };
};

/*
 * Whether the surface may render a benchmark-relative claim (a recommendation,
 * a "reduce X because Y is above benchmark" line). Phase 1: never, because no
 * benchmark carries defensible authority yet.
 */
export const canRenderOptimusBenchmarkClaim = (rawKey, metric) => {
  const contract = getOptimusMetricContract(rawKey, metric);
  return Boolean(contract && contract.recommendationEnabled);
};

/* True when the platform is graded at all (ALL and measurement surfaces are not). */
export const isOptimusGradablePlatform = (rawKey) => {
  const profile = getOptimusPlatformContract(rawKey);
  return Boolean(profile && profile.platformGradingEnabled);
};
